import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MutationStatus, useQueryClient} from 'react-query';
import {stringToPayloadFormat, useSnackbarHandler} from 'web-library';
import {useDispatch, useSelector} from 'react-redux';
import {
  useGetCandidateProfileDetails
} from '../../../api-refactored/user/useGetCandidateProfileDetails';
import {
  usePutCandidateProfileDetails
} from '../../../api-refactored/user/usePutCandidateProfileDetails';
import {
  IExamMasterCodeItem,
  LookupListApiItem,
  LookupListItem,
  UpdateCandidateProfileDetailsRequest,
} from '../../../api/user/model';
import {CandidateProfileFormData, CandidateProfileFormDefaultValues} from '../../../models';
import {getPreferredAddress, getPreferredContactType} from '../../../utils/ComponentUtils';
import {convertPrStatusToDisplayName} from '../../accountManagement/logic/usePrCitizenshipForm';
import {CreateCandidateFormLocationState} from './useCreateCandidateForm';
import {GET_CANDIDATE_PROFILE_DETAILS_QUERY_KEY} from '../../../constants/queryKeys';
import {selectFormEditMode, UpdateFormEdit} from '../../../store/formSlice';

import {useWOGAA} from '../../../hooks/wogaa';

interface UseCandidateProfileDetailsProps {
  state?: CreateCandidateFormLocationState;
}

interface UseCandidateProfileDetailsReturn {
  formDefaultValues: CandidateProfileFormDefaultValues | undefined;
  formDefaultValuesViewDetails: CandidateProfileFormDefaultValues | undefined;
  isEditProfileWindowOpen: boolean;
  isRequestProfileAmendmentOpen: boolean;
  noRegistration: boolean;
  hasPendingAmendmentRequest: boolean;
  refetchCandidateProfileDetails: () => void;
  updateCandidateDetailsApiStatus: MutationStatus;
  updateCandidateDetails: (formData: CandidateProfileFormData) => void;
  updateCandidateDetailsError: string;
}

/**
 * A hook used to get and update candidate profile details.
 */
export const useCandidateProfileDetails = ({
                                             state,
                                           }: UseCandidateProfileDetailsProps): UseCandidateProfileDetailsReturn => {
  const {data, refetch: refetchCandidateProfileDetails} = useGetCandidateProfileDetails();
  const {t} = useTranslation();
  const [updateProfileError, setUpdateProfileError] = useState('');

  const queryClient = useQueryClient();

  const {snackbarHandler} = useSnackbarHandler();

  const formDefaultValuesViewDetails:
      | CandidateProfileFormDefaultValues
      | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return {
      name: data.statutoryName,
      IdType: data.userIdType === 'NRIC_OR_FIN' ? 'I' : 'P',
      NricFin: data.candidateId,
      hanyupinyinName: '',
      gender: data.gender as IExamMasterCodeItem,
      dateOfBirth: data.dateOfBirth,
      email: data.email,
      race: data.race as IExamMasterCodeItem,
      nationality: data.nationality as IExamMasterCodeItem,
      prStatus: {
        ...data.prStatus,
        codeValueDescription: convertPrStatusToDisplayName(data.prStatus?.codeValueDescription),
      } as IExamMasterCodeItem,
      citizenship: data.citizenship as IExamMasterCodeItem,
      occupation: data.occupation as LookupListApiItem,
      school: {
        id: parseInt(data?.school?.code || '', 10),
        name: data?.school?.description,
      } as LookupListItem,

      // addresses
      houseOrBlkNo: data?.residentialAddress?.houseOrBlockNumber,
      streetName: data?.residentialAddress?.streetName,
      building: data?.residentialAddress?.buildingName,
      floor: data?.residentialAddress?.floorNumber,
      unit: data?.residentialAddress?.unitNumber,
      postalCode: data?.residentialAddress?.postalCode,
      foreignAddress1: data.foreignAddress1,
      foreignAddress2: data.foreignAddress2,
      foreignAddress3: data.foreignAddress3,
      preferredAddress: getPreferredAddress(data.localAddressPreferred),

      // contact numbers
      telephoneCountryCode: data?.telephoneNumber?.countryCode as IExamMasterCodeItem,
      telephoneContactNumber: data?.telephoneNumber?.number,
      mobileCountryCode: data?.mobileNumber?.countryCode as IExamMasterCodeItem,
      mobileContactNumber: data?.mobileNumber?.number,
      preferredTelephoneOrMobile: getPreferredContactType(data.mobileNumberPreferred),

      // next of kin
      nextOfKinName: data.nextOfKinName,
      nextOfKinCountryCode: data?.nextOfKinContactNumber
          ?.countryCode as IExamMasterCodeItem,
      nextOfKinContactNumber: data?.nextOfKinContactNumber?.number,
      nextOfKinRelationshipToCandidate: data?.nextOfKinRelationship as IExamMasterCodeItem,
    };
  }, [data]);

  const formDefaultValues: CandidateProfileFormDefaultValues | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }
    if (state?.redirectFromMyInfo) {
      return {
        name: state?.formData?.formDataDefaultValues?.name,
        IdType: state?.formData?.formDataDefaultValues?.IdType,
        NricFin: state?.formData?.formDataDefaultValues?.NricFin,
        hanyupinyinName: state?.formData?.formDataDefaultValues?.hanyupinyinName,
        gender: state?.formData?.formDataDefaultValues?.gender,
        dateOfBirth: state?.formData?.formDataDefaultValues?.dateOfBirth,
        email: state?.formData?.formDataDefaultValues?.email,
        race: state?.formData?.formDataDefaultValues?.race,
        nationality: state?.formData?.formDataDefaultValues?.nationality,
        prStatus: state?.formData?.formDataDefaultValues?.prStatus,
        citizenship: state?.formData?.formDataDefaultValues?.citizenship,
        occupation: state?.formData?.formDataDefaultValues?.occupation,
        school: {
          id: parseInt(data?.school?.code || '', 10),
          name: data?.school?.description,
        } as LookupListItem,

        // addresses
        houseOrBlkNo: state?.formData?.formDataDefaultValues?.houseOrBlkNo,
        streetName: state?.formData?.formDataDefaultValues?.streetName,
        building: state?.formData?.formDataDefaultValues?.building,
        floor: state?.formData?.formDataDefaultValues?.floor,
        unit: state?.formData?.formDataDefaultValues?.unit,
        postalCode: state?.formData?.formDataDefaultValues?.postalCode,
        foreignAddress1: data.foreignAddress1,
        foreignAddress2: data.foreignAddress2,
        foreignAddress3: data.foreignAddress3,
        preferredAddress: getPreferredAddress(data.localAddressPreferred),

        // contact numbers
        telephoneCountryCode: data?.telephoneNumber
            ?.countryCode as IExamMasterCodeItem,
        telephoneContactNumber: data?.telephoneNumber?.number,
        mobileCountryCode: state?.formData?.formDataDefaultValues?.mobileCountryCode,
        mobileContactNumber: state?.formData?.formDataDefaultValues?.mobileContactNumber,
        // CPEPOKPNEH-3058: added default values for phone number preference, as the new component for radio button was not able to capture the default value
        preferredTelephoneOrMobile: getPreferredContactType(data.mobileNumberPreferred),

        // next of kin
        nextOfKinName: data.nextOfKinName,
        nextOfKinCountryCode: data?.nextOfKinContactNumber
            ?.countryCode as IExamMasterCodeItem,
        nextOfKinContactNumber: data?.nextOfKinContactNumber?.number,
        nextOfKinRelationshipToCandidate: data?.nextOfKinRelationship as IExamMasterCodeItem,
      };
    }
    return formDefaultValuesViewDetails;
  }, [
    data,
    formDefaultValuesViewDetails,
    state?.formData?.formDataDefaultValues?.IdType,
    state?.formData?.formDataDefaultValues?.NricFin,
    state?.formData?.formDataDefaultValues?.building,
    state?.formData?.formDataDefaultValues?.citizenship,
    state?.formData?.formDataDefaultValues?.dateOfBirth,
    state?.formData?.formDataDefaultValues?.email,
    state?.formData?.formDataDefaultValues?.floor,
    state?.formData?.formDataDefaultValues?.gender,
    state?.formData?.formDataDefaultValues?.hanyupinyinName,
    state?.formData?.formDataDefaultValues?.houseOrBlkNo,
    state?.formData?.formDataDefaultValues?.mobileContactNumber,
    state?.formData?.formDataDefaultValues?.mobileCountryCode,
    state?.formData?.formDataDefaultValues?.name,
    state?.formData?.formDataDefaultValues?.nationality,
    state?.formData?.formDataDefaultValues?.occupation,
    state?.formData?.formDataDefaultValues?.postalCode,
    state?.formData?.formDataDefaultValues?.prStatus,
    state?.formData?.formDataDefaultValues?.race,
    state?.formData?.formDataDefaultValues?.streetName,
    state?.formData?.formDataDefaultValues?.unit,
    state?.redirectFromMyInfo,
  ]);

  const noRegistration = data?.noRegistration as boolean;

  const isRequestProfileAmendmentOpen = data?.requestProfileAmendmentOpen as boolean;

  const isEditProfileWindowOpen = data?.editProfileWindowOpen as boolean;

  const hasPendingAmendmentRequest = data?.hasPendingAmendmentRequest as boolean;

  const dispatch = useDispatch();
  const editMode = useSelector(selectFormEditMode);
// wogaa transaction end for candidate profile edit
  const {transactionEnd} = useWOGAA('myexamsseab-6471');

  const {mutate, status} = usePutCandidateProfileDetails({
    onSuccess: () => {
      // Pass user uuid as agencyTxnId for WOGAA sentiments tracking
      transactionEnd(data?.uuid);
      setUpdateProfileError('');
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('profile->>CandidateProfileDetail.SubmitSuccessMsg'),
      });
      queryClient.invalidateQueries(GET_CANDIDATE_PROFILE_DETAILS_QUERY_KEY);
      dispatch(UpdateFormEdit(!editMode));
    },
    onError: (err?) => {
      setUpdateProfileError(err?.response?.data.message!);
    },
  });

  const updateCandidateDetails = (formData: CandidateProfileFormData) => {
    const putRequestBody: UpdateCandidateProfileDetailsRequest = {
      candidateId: formData.NricFin,
      userIdType: formData.IdType === 'I' ? 'NRIC_OR_FIN' : 'PASSPORT',
      statutoryName: formData.name,
      hanyuPinyinName: '',
      gender: formData.gender,
      email: formData.email,
      race: formData.race,
      nationality: formData.nationality,
      prStatus: formData.prStatus
          ? {
            codeId: formData.prStatus.codeId,
            codeValue: formData.prStatus.codeValue,
            codeValueDescription:
                formData.prStatus.codeValueDescription === 'FOREIGNER'
                    ? 'ALIEN'
                    : formData.prStatus.codeValueDescription,
          }
          : undefined,
      dateOfBirth: stringToPayloadFormat((formData.dateOfBirth || data?.dateOfBirth) as string),
      citizenship: formData.citizenship,
      occupation: formData.occupation,

      // addresses
      residentialAddress: {
        houseOrBlockNumber: formData.houseOrBlkNo,
        floorNumber: formData.floor,
        unitNumber: formData.unit,
        buildingName: formData.building,
        streetName: formData.streetName,
        postalCode: formData.postalCode,
      },
      foreignAddress1: formData.foreignAddress1,
      foreignAddress2: formData.foreignAddress2,
      foreignAddress3: formData.foreignAddress3,
      localAddressPreferred: formData.preferredAddress === 'L',

      // contact numbers
      mobileNumber: {
        countryCode: formData.mobileCountryCode,
        number: formData.mobileContactNumber,
      },
      telephoneNumber: {
        countryCode: formData.telephoneCountryCode,
        number: formData.telephoneContactNumber,
      },
      mobileNumberPreferred: formData.preferredTelephoneOrMobile === 'M',

      // next of kin
      nextOfKinName: formData.nextOfKinName,
      nextOfKinContactNumber: {
        countryCode: formData.nextOfKinCountryCode,
        number: formData.nextOfKinContactNumber,
      },
      nextOfKinRelationship: formData.nextOfKinRelationshipToCandidate,
      school: formData?.school
          ? {
            code: `${formData.school.id}`,
            description: formData.school.name,
          }
          : undefined,
    };

    mutate(putRequestBody);
  };

  return {
    formDefaultValues,
    formDefaultValuesViewDetails,
    isEditProfileWindowOpen,
    isRequestProfileAmendmentOpen,
    noRegistration,
    hasPendingAmendmentRequest,
    refetchCandidateProfileDetails,
    updateCandidateDetailsApiStatus: status,
    updateCandidateDetails,
    updateCandidateDetailsError: updateProfileError,
  };
};
